/**
 * HAVVA Camera Service
 * Handles API calls for HAVVA Camera Management System
 *
 * All endpoints go through: middleware.kapitani.co/api/client/{clientId}/...
 *
 * API Reference (IE-Middleware-HAVVA):
 * 1. Add Camera    - POST   /register
 * 2. List Camera   - GET    /device-list      ?farmerId&farmId&vendorId
 *                     (without farmId: every camera of that farmer's account)
 * 3. Edit Camera   - PATCH  /edit
 * 4. Delete Camera - DELETE /delete
 * 5. Analysis History - GET /analysis-history  ?farmerId&farmId&vendorId
 * 6. Detail Analysis  - GET /detail-analysis   ?farmerId&farmId&vendorId&id
 * 7. Upload Image  - POST   /upload-image      (multipart: farmerId, farmId, vendorId, image)
 *
 * Every call goes through request(): 30 s timeout (120 s for upload-image),
 * and failures come back as { success: false, message, code, httpStatus }.
 *
 * The middleware is fragile. The camera list of one farm + farmer pair (or of
 * a farmer's whole account, farmId 'all') can be read through
 * listCamerasCached(), which keeps each answer in memory for 5 minutes; call
 * invalidateCameraCache() after a camera is added, edited or deleted, and
 * setCameraCacheOwner() with the logged-in user before reading.
 */

import { IOT_VENDORS, KAPITANI_BASE_URL } from '../config/iotVendors';

// Get HAVVA config from centralized IoT vendor configuration
const HAVVA_VENDOR = IOT_VENDORS.HAVVA;

// The middleware can hang without ever answering, so every call is time-boxed.
const DEFAULT_TIMEOUT_MS = 30000;   // camera CRUD, history, detail
const UPLOAD_TIMEOUT_MS = 120000;   // upload-image waits for HAVVA's analysis (7-25 s typical)

// How long a device-list answer is reused (memory only; a page reload clears it)
const CAMERA_LIST_CACHE_MS = 5 * 60 * 1000;

class HAVVAService {
  constructor() {
    this.baseUrl = KAPITANI_BASE_URL;
    this.clientId = HAVVA_VENDOR.clientId;
    this.vendorId = HAVVA_VENDOR.vendorId;
    this.debug = true;
    // device-list cache, one entry per farm + farmer pair
    this.cameraListCache = new Map();    // key -> { farmId, farmerId, at, data }
    this.cameraListInFlight = new Map(); // key -> { farmId, promise, signal } of the running request
    this.cameraListLatest = new Map();   // key -> { farmId, token } of the newest request
    this.cameraCacheOwner = null;        // user the cached lists were loaded for
  }

  /**
   * Build the base URL for middleware API calls
   */
  getClientUrl(endpoint) {
    return `${this.baseUrl}/client/${this.clientId}${endpoint}`;
  }

  /**
   * Build an Error that carries a machine-readable code
   * code: 'timeout' | 'cancelled' | 'network' | 'http' | 'invalid-response' | 'vendor-message' | 'skipped'
   */
  buildError(code, message, httpStatus = null) {
    const error = new Error(message);
    error.isHavvaError = true;
    error.code = code;
    error.httpStatus = httpStatus;
    return error;
  }

  /**
   * Standard failure result returned by every API method
   */
  failure(error, fallbackMessage) {
    return {
      success: false,
      message: error.message || fallbackMessage,
      code: error.isHavvaError ? error.code : 'unknown',
      httpStatus: error.httpStatus || null,
      error
    };
  }

  /**
   * fetch + JSON parse with a timeout and optional caller cancellation.
   * Resolves with the parsed body; rejects with an Error from buildError().
   *
   * @param {string} url - Full request URL
   * @param {Object} fetchOptions - Options passed to fetch (method, headers, body)
   * @param {Object} options
   * @param {number} options.timeoutMs - Abort after this long (default 30 s)
   * @param {AbortSignal} options.signal - Caller's signal (e.g. a Cancel button)
   * @param {string} options.serviceLabel - Name used in user-facing messages
   * @param {string} options.actionLabel - Used in the "cancelled" message
   */
  async request(url, fetchOptions = {}, options = {}) {
    const {
      timeoutMs = DEFAULT_TIMEOUT_MS,
      signal = null,
      serviceLabel = 'HAVVA service',
      actionLabel = 'Request'
    } = options;

    const controller = new AbortController();
    let timedOut = false;
    const timer = setTimeout(() => {
      timedOut = true;
      controller.abort();
    }, timeoutMs);

    const onCallerAbort = () => controller.abort();
    if (signal) {
      if (signal.aborted) controller.abort();
      else signal.addEventListener('abort', onCallerAbort);
    }

    try {
      const response = await fetch(url, { ...fetchOptions, signal: controller.signal });

      if (!response.ok) {
        if (response.status >= 500) {
          throw this.buildError(
            'http',
            `The ${serviceLabel} is not responding (HTTP ${response.status}). Please try again later.`,
            response.status
          );
        }
        const errorData = await response.json().catch(() => ({}));
        throw this.buildError(
          'http',
          errorData?.error?.message || errorData?.message || `HTTP ${response.status}: ${response.statusText}`,
          response.status
        );
      }

      try {
        return await response.json();
      } catch (parseError) {
        if (parseError?.name === 'AbortError') throw parseError;
        throw this.buildError('invalid-response', `The ${serviceLabel} returned an unreadable response.`, response.status);
      }
    } catch (error) {
      // Already classified above. (Do not test error.code here: a DOMException
      // AbortError carries its own legacy numeric code.)
      if (error?.isHavvaError) throw error;

      if (error?.name === 'AbortError' || controller.signal.aborted) {
        if (timedOut) {
          throw this.buildError(
            'timeout',
            `The ${serviceLabel} did not respond within ${Math.round(timeoutMs / 1000)} seconds. Please try again later.`
          );
        }
        throw this.buildError('cancelled', `${actionLabel} cancelled.`);
      }

      // fetch rejects with a TypeError when the request never completes
      // (offline, DNS, or a gateway error page without CORS headers)
      throw this.buildError(
        'network',
        `Could not reach the ${serviceLabel}. Check your internet connection; if it is working, the service may be down. Please try again later.`
      );
    } finally {
      clearTimeout(timer);
      if (signal) signal.removeEventListener('abort', onCallerAbort);
    }
  }

  /**
   * API 1: Add Camera (Register new camera)
   * Method: POST
   * URL: /api/client/{clientId}/register
   * Body-JSON: { cameraId, note, farmerId, farmId, vendorId }
   */
  async registerCamera(cameraData) {
    try {
      if (this.debug) {
        console.log('[HAVVA] Registering camera:', cameraData);
      }

      const payload = {
        cameraId: cameraData.cameraId,
        note: cameraData.note,
        farmerId: cameraData.farmerId,
        farmId: cameraData.farmId,
        vendorId: cameraData.vendorId || this.vendorId
      };

      const result = await this.request(this.getClientUrl('/register'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });

      if (this.debug) {
        console.log('[HAVVA] Camera registered:', result);
      }

      return { success: true, data: result };

    } catch (error) {
      console.error('[HAVVA] Error registering camera:', error);
      return this.failure(error, 'Failed to register camera');
    }
  }

  /**
   * API 2: List Camera (Get all registered cameras)
   * Method: GET
   * URL: /api/client/{clientId}/device-list?farmerId=X&farmId=X&vendorId=X
   *
   * From Postman: query params farmerId, farmId, vendorId
   *
   * farmId is left out when it is empty, 'all' or 'ALL' (same rule as the LED
   * and sensor lists). The middleware then returns every camera registered
   * under that farmerId, across all farms (verified live, 2 Oct 2026).
   *
   * Pass { signal } (AbortController) to drop the request when its answer is
   * no longer wanted; the result then carries code: 'cancelled'.
   */
  async listCameras(farmId, farmerId, { signal = null } = {}) {
    try {
      if (this.debug) {
        console.log('[HAVVA] Fetching camera list - farmId:', farmId, 'farmerId:', farmerId);
      }

      // Same parameter order as before for a single farm: farmerId, farmId, vendorId
      const params = new URLSearchParams({ farmerId: farmerId });
      if (!this.isAllFarms(farmId)) {
        params.set('farmId', farmId);
      }
      params.set('vendorId', this.vendorId);

      const url = `${this.getClientUrl('/device-list')}?${params}`;
      const result = await this.request(url, { method: 'GET' }, { signal, actionLabel: 'Camera list request' });

      if (this.debug) {
        console.log('[HAVVA] Retrieved cameras:', result);
      }

      // API returns { data: [...] } or array directly
      const cameras = result?.data || result;

      // The middleware reports some failures as HTTP 200 with only a message,
      // e.g. {"message":"Missing 'vendorId'"} - that is an error, not "no cameras".
      // So is any other reply that is not a list ({}, null, {"data":null}, ...):
      // the number of cameras is then unknown, not zero.
      if (!Array.isArray(cameras)) {
        const vendorMessage = result?.error?.message || result?.message;
        if (vendorMessage) {
          throw this.buildError('vendor-message', String(vendorMessage), 200);
        }
        throw this.buildError('invalid-response', 'The HAVVA service returned an unexpected response.', 200);
      }

      return { success: true, data: cameras };

    } catch (error) {
      // A cancelled request is the caller's own doing, not an error
      if (error?.code !== 'cancelled') console.error('[HAVVA] Error listing cameras:', error);
      return this.failure(error, 'Failed to list cameras');
    }
  }

  /**
   * True when the farm id stands for "every farm of the account": empty,
   * 'all' or 'ALL'. The device-list request is then sent without farmId.
   */
  isAllFarms(farmId) {
    return farmId === null || farmId === undefined || farmId === '' || farmId === 'all' || farmId === 'ALL';
  }

  // Farm id as kept in the camera cache: one spelling ('all') for a whole account
  cameraCacheFarmId(farmId) {
    return this.isAllFarms(farmId) ? 'all' : farmId;
  }

  cameraListKey(farmId, farmerId) {
    return `${this.cameraCacheFarmId(farmId)}__${farmerId}`;
  }

  /**
   * List Camera with a 5-minute in-memory cache per farm + farmer pair.
   * Only successful answers are kept. A request that is already running for
   * the same pair is shared instead of sent twice.
   *
   * @param {string|number} farmId - A farm, or empty / 'all' / 'ALL' for the
   *   whole account of farmerId (one cache entry, whichever spelling is used)
   * @param {string} farmerId
   * @param {Object} options
   * @param {boolean} options.force - Skip the cached answer and ask the
   *   middleware (manual refresh). The fresh answer replaces the cached one.
   * @param {boolean} options.cacheOnly - Never ask the middleware: the cached
   *   answer, or a failure with code 'skipped' when there is none.
   * @param {AbortSignal} options.signal - Drops the request when aborted
   *   (the load it belongs to has been superseded)
   * @returns same as listCameras(), plus cached: true for a cached answer
   */
  async listCamerasCached(farmId, farmerId, { force = false, cacheOnly = false, signal = null } = {}) {
    const key = this.cameraListKey(farmId, farmerId);
    // What the cache remembers as the farm of this list ('all' for a whole account)
    const cacheFarmId = this.cameraCacheFarmId(farmId);

    if (!force || cacheOnly) {
      const cached = this.cameraListCache.get(key);
      if (cached && Date.now() - cached.at < CAMERA_LIST_CACHE_MS) {
        return { success: true, data: cached.data, cached: true };
      }
    }
    if (cacheOnly) {
      return this.failure(this.buildError('skipped', 'The HAVVA service is not responding. Please try again later.'), 'Failed to list cameras');
    }
    if (!force) {
      // Share a request that is already running, unless it has been dropped
      const running = this.cameraListInFlight.get(key);
      if (running && !running.signal?.aborted) return running.promise;
    }

    // Only the newest request of a pair may write the cache: an older one
    // that answers late (or one overtaken by invalidateCameraCache) must not
    // put a stale list back.
    const token = {};
    this.cameraListLatest.set(key, { farmId: cacheFarmId, token });

    const promise = this.listCameras(farmId, farmerId, { signal }).then((result) => {
      if (this.cameraListLatest.get(key)?.token === token) {
        if (result.success) {
          this.cameraListCache.set(key, { farmId: cacheFarmId, farmerId, at: Date.now(), data: result.data });
        } else if (result.code !== 'cancelled') {
          // A dropped request says nothing about the list; a failed one does
          this.cameraListCache.delete(key);
        }
      }
      if (this.cameraListInFlight.get(key)?.promise === promise) {
        this.cameraListInFlight.delete(key);
      }
      return result;
    });

    this.cameraListInFlight.set(key, { farmId: cacheFarmId, promise, signal });
    return promise;
  }

  /**
   * The camera lists still fresh in the cache, as [{ farmId, farmerId, data }]
   * - e.g. of a farm that was looked at a moment ago. Asks nothing of the
   * middleware. A whole-account list has farmId 'all'.
   */
  getCachedCameraLists() {
    const now = Date.now();
    return [...this.cameraListCache.values()]
      .filter(entry => now - entry.at < CAMERA_LIST_CACHE_MS)
      .map(entry => ({ farmId: entry.farmId, farmerId: entry.farmerId, data: entry.data }));
  }

  /**
   * Say whose camera lists the cache holds. The service outlives a logout
   * (the page is not reloaded), so when another user logs in on the same tab
   * everything cached for the previous one is forgotten.
   *
   * @param {string|null} ownerId - Id of the logged-in user
   */
  setCameraCacheOwner(ownerId) {
    const owner = ownerId === null || ownerId === undefined ? null : String(ownerId);
    if (owner !== this.cameraCacheOwner) {
      this.invalidateCameraCache();
      this.cameraCacheOwner = owner;
    }
  }

  /**
   * Forget cached camera lists, so the next listCamerasCached() asks the
   * middleware. Requests still running are cut off from the cache as well.
   * Call it after add / edit / delete with the farm(s) involved. A
   * whole-account list (farmId 'all') holds the cameras of every farm, so it
   * is forgotten whichever farms are named.
   *
   * @param {Array<string|number>|null} farmIds - Farms to forget; null = everything
   */
  invalidateCameraCache(farmIds = null) {
    const farms = Array.isArray(farmIds)
      ? new Set(farmIds.filter(id => id !== null && id !== undefined && id !== '').map(String))
      : null;
    [this.cameraListCache, this.cameraListInFlight, this.cameraListLatest].forEach((map) => {
      [...map.entries()].forEach(([key, entry]) => {
        if (farms === null || this.isAllFarms(entry.farmId) || farms.has(String(entry.farmId))) map.delete(key);
      });
    });
  }

  /**
   * API 3: Edit Camera (Update existing camera info)
   * Method: PATCH
   * URL: /api/client/{clientId}/edit
   * Body-JSON: { cameraId, note, farmerId, farmId, vendorId, id }
   */
  async editCamera(cameraData) {
    try {
      if (this.debug) {
        console.log('[HAVVA] Editing camera:', cameraData);
      }

      const payload = {
        id: cameraData.id,
        cameraId: cameraData.cameraId,
        note: cameraData.note,
        farmerId: cameraData.farmerId,
        farmId: cameraData.farmId,
        vendorId: cameraData.vendorId || this.vendorId
      };

      const result = await this.request(this.getClientUrl('/edit'), {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });

      if (this.debug) {
        console.log('[HAVVA] Camera updated:', result);
      }

      return { success: true, data: result };

    } catch (error) {
      console.error('[HAVVA] Error editing camera:', error);
      return this.failure(error, 'Failed to edit camera');
    }
  }

  /**
   * API 4: Delete Camera (Remove camera from system)
   * Method: DELETE
   * URL: /api/client/{clientId}/delete
   * Body-JSON: { farmerId, farmId, vendorId, id }
   */
  async deleteCamera(deleteData) {
    try {
      if (this.debug) {
        console.log('[HAVVA] Deleting camera:', deleteData);
      }

      const payload = {
        farmerId: deleteData.farmerId,
        farmId: deleteData.farmId,
        vendorId: deleteData.vendorId || this.vendorId,
        id: deleteData.id
      };

      const result = await this.request(this.getClientUrl('/delete'), {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });

      if (this.debug) {
        console.log('[HAVVA] Camera deleted:', result);
      }

      return { success: true, data: result };

    } catch (error) {
      console.error('[HAVVA] Error deleting camera:', error);
      return this.failure(error, 'Failed to delete camera');
    }
  }

  /**
   * API 5: Analysis History
   * Method: GET
   * URL: /api/client/{clientId}/analysis-history?farmerId=X&farmId=X&vendorId=X
   *
   * From Postman screenshot: query params farmerId, farmId, vendorId
   * Response: { data: [...] }
   */
  async getAnalysisHistory(farmerId, farmId) {
    try {
      if (this.debug) {
        console.log('[HAVVA] Fetching analysis history - farmerId:', farmerId, 'farmId:', farmId);
      }

      const params = new URLSearchParams({
        farmerId: farmerId,
        farmId: farmId,
        vendorId: this.vendorId
      });

      const url = `${this.getClientUrl('/analysis-history')}?${params}`;
      const result = await this.request(url, { method: 'GET' });

      if (this.debug) {
        console.log('[HAVVA] Analysis history:', result);
      }

      const history = result?.data || result;
      return { success: true, data: Array.isArray(history) ? history : [] };

    } catch (error) {
      console.error('[HAVVA] Error fetching analysis history:', error);
      return this.failure(error, 'Failed to fetch analysis history');
    }
  }

  /**
   * API 6: Detail Analysis
   * Method: GET
   * URL: /api/client/{clientId}/detail-analysis?farmerId=X&farmId=X&vendorId=X&id=X
   *
   * From Postman screenshot: query params farmerId, farmId, vendorId, id
   * Response: { cameraId, timeStamp, leafDamage, diseaseSpreadRate,
   *             plantHeightVariation, leafColorDeviation, growtRateReduction,
   *             recommendedActivity, image }
   */
  async getDetailAnalysis(farmerId, farmId, id) {
    try {
      if (this.debug) {
        console.log('[HAVVA] Fetching detail analysis - farmerId:', farmerId, 'farmId:', farmId, 'id:', id);
      }

      const params = new URLSearchParams({
        farmerId: farmerId,
        farmId: farmId,
        vendorId: this.vendorId,
        id: id
      });

      const url = `${this.getClientUrl('/detail-analysis')}?${params}`;
      const result = await this.request(url, { method: 'GET' });

      if (this.debug) {
        console.log('[HAVVA] Detail analysis:', result);
      }

      return { success: true, data: result };

    } catch (error) {
      console.error('[HAVVA] Error fetching detail analysis:', error);
      return this.failure(error, 'Failed to fetch detail analysis');
    }
  }

  /**
   * API 7: Upload Image (Upload image for HAVVA AI analysis)
   * Method: POST
   * URL: /api/client/{clientId}/upload-image
   * Content-Type: multipart/form-data
   * Body: { farmerId, farmId, vendorId, image (File) }
   * Response (live, Oct 2026): { id, cameraId, timeStamp, healthy_percentage,
   *   unhealthy_percentage, recommendedActivity, image, ... } - any analysis
   *   field may be the string "N/A"; run it through normalizeHavvaAnalysis().
   *
   * Times out after 120 s. Pass { signal } (AbortController) to let the user cancel.
   * On failure the result carries code: 'timeout' | 'cancelled' | 'http' | 'network' | ...
   */
  async uploadImage(imageFile, farmerId, farmId, { signal = null } = {}) {
    try {
      if (this.debug) {
        console.log('[HAVVA] Uploading image:', imageFile.name, 'farmerId:', farmerId, 'farmId:', farmId);
      }

      const formData = new FormData();
      formData.append('farmerId', farmerId);
      formData.append('farmId', farmId);
      formData.append('vendorId', this.vendorId);
      formData.append('image', imageFile);

      const result = await this.request(this.getClientUrl('/upload-image'), {
        method: 'POST',
        // Do NOT set Content-Type header - browser sets it with boundary for multipart
        body: formData
      }, {
        timeoutMs: UPLOAD_TIMEOUT_MS,
        signal,
        serviceLabel: 'analysis service',
        actionLabel: 'Upload'
      });

      if (this.debug) {
        console.log('[HAVVA] Upload result:', result);
      }

      return { success: true, data: result };

    } catch (error) {
      console.error('[HAVVA] Error uploading image:', error);
      return this.failure(error, 'Failed to upload image');
    }
  }
}

// Create and export a singleton instance
const havvaService = new HAVVAService();
export default havvaService;
